import axios from 'axios';
import header from '../api/auth/header';

const API_URL = process.env.API_URL + '/api/v3';

/**
 * Pengecekan persetujuan user: SNK, Kebijakan Privasi, dan syarat Tilaka.
 *
 * Dipakai bersama oleh router guard dan halaman login supaya keduanya memakai aturan
 * yang sama persis. Endpoint yang dipanggil di sini hanya bermiddleware auth:api —
 * bukan snk-agreed — karena kalau tidak, untuk mengetahui user belum menyetujui SNK
 * kita justru diblokir oleh SNK itu sendiri.
 *
 * Mengembalikan { needAgreement, unauthenticated }:
 * - needAgreement  : ada persetujuan yang belum diberikan, arahkan ke halaman Agreement.
 * - unauthenticated: token sudah tidak berlaku, sesi harus dibersihkan.
 */

/**
 * Deteksi "token tidak berlaku" dari body response.
 *
 * Tidak cukup memeriksa meta.code 401. Ketika middleware auth:api menolak token,
 * Laravel melempar AuthenticationException yang ditangani exception handler umum,
 * dan hasilnya TIDAK berbentuk 401 sama sekali:
 *
 *   HTTP 200  { "meta": { "code": 500, "success": false,
 *                         "message": "Terjadi kesalahan pada server." },
 *               "data": { "error": "Unauthenticated." } }
 *
 * Satu-satunya penanda pasti ada di data.error. Tanpa mengenali bentuk ini,
 * response tersebut lolos sebagai response biasa, lalu dibaca sebagai "is_agreed
 * tidak ada" alias belum menyetujui — dan user dikunci di halaman Agreement.
 */
export function isUnauthorizedBody(body) {
  if (!body) {
    return false;
  }

  if (body.meta && body.meta.code == 401) {
    return true;
  }

  if (body.data && body.data.error === 'Unauthenticated.') {
    return true;
  }

  const message = body.meta && body.meta.message;

  return typeof message === 'string' && message.indexOf('Unauthenticated') !== -1;
}

export function isUnauthorizedError(err) {
  const res = err && err.response;

  if (!res) {
    return false;
  }

  return res.status === 401 || isUnauthorizedBody(res.data);
}

/**
 * Baca status persetujuan dari body response.
 *
 * Mengembalikan true/false hanya bila server memang menyatakannya — termasuk pada
 * body error "belum ada persetujuan", yang tetap memuat is_agreed: false. Bila
 * responsnya tidak bicara soal itu (error tak terduga, payload lain), hasilnya
 * null: statusnya tidak diketahui, dan menebak "belum setuju" hanya akan
 * mengurung user di halaman Agreement tanpa jalan keluar.
 */
export function readAgreedFlag(body) {
  const data = body && body.data;

  if (data && typeof data.is_agreed === 'boolean') {
    return data.is_agreed;
  }

  if (body && body.meta && body.meta.success === true && data) {
    return !!data.is_agreed;
  }

  return null;
}

// 403/404 pada endpoint "current agreement" berarti belum ada persetujuan tersimpan.
function isAgreementMissingError(err) {
  const res = err && err.response;

  return !!(res && (res.status === 404 || res.status === 403));
}

function get(path) {
  return axios.get(API_URL + path, { headers: header() });
}

export default async function checkAgreementStatus(user) {
  const status = {
    needAgreement: false,
    unauthenticated: false,
  };

  const steps = [
    { content: '/content-snk/active', agreement: '/users/agreement/current' },
    { content: '/content-privacy/active', agreement: '/users/privacy/agreement/current' },
  ];

  // Syarat Tilaka hanya berlaku untuk nasabah (role_id 3 dan 4) dan tidak punya
  // endpoint konten aktif, jadi persetujuannya langsung dicek.
  if (user && (user.role_id == 3 || user.role_id == 4)) {
    steps.push({ content: null, agreement: '/users/tilaka/agreement/current' });
  }

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];

    if (status.needAgreement || status.unauthenticated) {
      break;
    }

    // Kalau kontennya belum dipublikasikan, tidak ada yang perlu disetujui.
    // Kegagalan pada satu langkah tidak membatalkan pengecekan langkah berikutnya.
    if (step.content) {
      try {
        const contentRes = await get(step.content);

        if (isUnauthorizedBody(contentRes.data)) {
          status.unauthenticated = true;
          break;
        }

        const contentExists = contentRes.data && contentRes.data.data && contentRes.data.data.id;

        if (!contentExists) {
          continue;
        }
      } catch (contentErr) {
        if (isUnauthorizedError(contentErr)) {
          status.unauthenticated = true;
          break;
        }

        continue;
      }
    }

    try {
      const agreeRes = await get(step.agreement);

      if (isUnauthorizedBody(agreeRes.data)) {
        status.unauthenticated = true;
        break;
      }

      // Hanya pernyataan tegas dari server yang boleh mengunci user ke halaman
      // Agreement. Status yang tidak diketahui (null) dilewati saja.
      if (readAgreedFlag(agreeRes.data) === false) {
        status.needAgreement = true;
      }
    } catch (agreeErr) {
      if (isUnauthorizedError(agreeErr)) {
        status.unauthenticated = true;
        break;
      }

      if (isAgreementMissingError(agreeErr)) {
        status.needAgreement = true;
      }
    }
  }

  return status;
}



// WEBPACK FOOTER //
// ./src/helpers/agreementStatus.js